import { useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Overlay from 'react-bootstrap/Overlay';
import Popover from 'react-bootstrap/Popover';
import Form from 'react-bootstrap/Form';
import { Spinner } from '../../../../../components/Spinner';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';

const AMOUNT_RANGE_MIN_VALUE = 10;

function NewFundingPopoverNewLinkForm({ fundingId, fundingOutstandingAmount, refreshFundingData }) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const target = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const [spinner, setSpinner] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState(fundingOutstandingAmount);
  const [paymentByAdmin, setPaymentByAdmin] = useState(false);
  const [allowAmountChange, setAllowAmountChange] = useState(false);
  const [minimalAmount, setMinimalAmount] = useState(null);

  const togglePopover = () => {
    setPopoverOpen(!popoverOpen);
    setPaymentAmount(fundingOutstandingAmount);
    setPaymentByAdmin(false);
    setAllowAmountChange(false);
    setMinimalAmount(null);
  };

  const createLink = async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/ad_hoc_payment/create_link`,
      method: 'POST',
      data: JSON.stringify({
        fundingId,
        amount: parseInt(paymentAmount, 10),
        paymentByAdmin,
        allowAmountChange,
        minimalAmount: minimalAmount ? parseInt(minimalAmount, 10) : null,
      }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();

      toast.success('Link created successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
      togglePopover();
    }
  };

  const handleCreate = async () => {
    if (!paymentAmount) {
      toast.error('Payment amount is required');

      return;
    }

    if (
      Number(paymentAmount) < AMOUNT_RANGE_MIN_VALUE ||
      Number(paymentAmount) > Number(fundingOutstandingAmount)
    ) {
      toast.error(
        `Payment amount is too ${
          paymentAmount > fundingOutstandingAmount ? 'high' : 'low'
        }, it should be in the range of $${AMOUNT_RANGE_MIN_VALUE}
           to $${fundingOutstandingAmount}`,
      );

      return;
    }

    if (
      allowAmountChange &&
      (Number(minimalAmount) > Number(paymentAmount) ||
        Number(minimalAmount) < AMOUNT_RANGE_MIN_VALUE)
    ) {
      toast.error(
        `Minimal amount is too ${
          Number(minimalAmount) > Number(paymentAmount) ? 'high' : 'low'
        }, it should be in the range of $${AMOUNT_RANGE_MIN_VALUE}
          to $${paymentAmount}`,
      );

      return;
    }

    await createLink();
  };

  return (
    <>
      <Button ref={target} className="bo-common-button" onClick={togglePopover}>
        Create New Link
      </Button>
      <Overlay
        show={popoverOpen}
        target={target.current}
        placement="right"
        popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 4] } }] }}
      >
        {props => (
          <Popover {...props} className="custom-popover">
            <Popover.Body className="bo-common-popover-form-menu bo-w-220">
              <Spinner visible={spinner} size={36}>
                <div>
                  <Form.Label className="bo-common-popover-form-menu-label">
                    Payment Amount:
                  </Form.Label>
                  <NumericFormat
                    type="text"
                    className="bo-common-popover-form-menu-input"
                    value={paymentAmount}
                    onChange={e => setPaymentAmount(e.target.value)}
                    customInput={Form.Control}
                    allowNegative={false}
                    decimalScale={0}
                    allowLeadingZeros
                  />
                </div>
                <div className="d-flex align-items-center mt-2">
                  <Form.Check
                    checked={paymentByAdmin}
                    onChange={e => setPaymentByAdmin(e.target.checked)}
                  />
                  <Form.Label className="bo-common-popover-form-menu-label">
                    Payment by admin on behalf of consumer
                  </Form.Label>
                </div>
                <div className="d-flex align-items-center mt-2">
                  <Form.Check
                    checked={allowAmountChange}
                    onChange={e => setAllowAmountChange(e.target.checked)}
                  />
                  <Form.Label className="bo-common-popover-form-menu-label">
                    User can change amount
                  </Form.Label>
                </div>
                <div>
                  <Form.Label className="bo-common-popover-form-menu-label">
                    Minimal Amount:
                  </Form.Label>
                  <NumericFormat
                    disabled={!allowAmountChange}
                    type="text"
                    className="bo-common-popover-form-menu-input"
                    value={minimalAmount}
                    onChange={e => setMinimalAmount(e.target.value)}
                    customInput={Form.Control}
                    allowNegative={false}
                    decimalScale={0}
                    allowLeadingZeros
                  />
                </div>
                <div className="mt-3 d-flex justify-content-between">
                  <Button
                    className="bo-common-button"
                    onClick={handleCreate}
                    disabled={
                      paymentAmount === null || paymentAmount === undefined || paymentAmount === ''
                    }
                  >
                    Create
                  </Button>
                  <Button className="bo-common-button" onClick={togglePopover}>
                    Cancel
                  </Button>
                </div>
              </Spinner>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
}

export default NewFundingPopoverNewLinkForm;
